import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { motion } from "framer-motion";
import GridLayout from "react-grid-layout";
import {
  FaFlask,
  FaComments,
  FaSitemap,
  FaBuilding,
  FaUsers,
  FaShieldAlt,
  FaEnvelope,
  FaCog,
  FaCheck,
  FaGripVertical,
  FaCodeBranch,
  FaChartBar,
  FaProjectDiagram,
  FaFilePdf,
} from "react-icons/fa";
import { useSidebarStore, AppLayoutItem } from "@/hooks/use-sidebar/store";
import { useGestureScroll } from "@/hooks/use-gesture-scroll";
import { setDragPayload, createAppDragPayload } from "@/utils/drag-types";
import { getResourceIcon } from "@/utils/resource-icons";
import {
  useFocusedPanel,
  useSplitViewActive,
  useTabsStore as useSplitViewStore,
} from "@/hooks/use-tabs-store";
import { getPanelTypeFromRoute } from "@/hooks/use-panel-route";
import "react-grid-layout/css/styles.css";

interface AppFavorite {
  id: string;
  title: string;
  icon: React.ReactNode;
  link: string;
}

// Default app favorites - order can be customized by user
const defaultAppFavorites: AppFavorite[] = [
  {
    id: "trackers",
    title: "Trackers",
    icon: getResourceIcon("tracker"),
    link: "/trackers",
  },
  {
    id: "dashboards",
    title: "Dashboards",
    icon: getResourceIcon("dashboard"),
    link: "/dashboards",
  },
  {
    id: "widgets",
    title: "Widgets",
    icon: getResourceIcon("widget"),
    link: "/widgets",
  },
  {
    id: "alerts",
    title: "Alerts",
    icon: getResourceIcon("alert"),
    link: "/alerts",
  },
  {
    id: "playground",
    title: "Playground",
    icon: <FaFlask size={16} />,
    link: "/search",
  },
  {
    id: "chat",
    title: "Chat",
    icon: <FaComments size={16} />,
    link: "/chat",
  },
  {
    id: "enrichments",
    title: "Enrichments",
    icon: getResourceIcon("enrichment"),
    link: "/enrichments",
  },
  {
    id: "investigations",
    title: "Investigations",
    icon: <FaProjectDiagram size={16} />,
    link: "/investigations",
  },
  {
    id: "reports",
    title: "Rapports",
    icon: <FaFilePdf size={16} />,
    link: "/reports",
  },
  {
    id: "branches",
    title: "Branches",
    icon: <FaCodeBranch size={16} />,
    link: "/branches",
  },
  {
    id: "plan",
    title: "Plan & Usage",
    icon: <FaChartBar size={16} />,
    link: "/plan",
  },
  {
    id: "workspaces",
    title: "Workspaces",
    icon: <FaBuilding size={16} />,
    link: "/workspaces",
  },
  {
    id: "organization",
    title: "Organization",
    icon: <FaSitemap size={16} />,
    link: "/organization",
  },
  {
    id: "members",
    title: "Members",
    icon: <FaUsers size={16} />,
    link: "/members",
  },
  {
    id: "api_tokens",
    title: "API Tokens",
    icon: <FaShieldAlt size={16} />,
    link: "/tokens",
  },
  {
    id: "channels",
    title: "Channels",
    icon: <FaEnvelope size={16} />,
    link: "/channels",
  },
  {
    id: "settings",
    title: "Settings",
    icon: <FaCog size={16} />,
    link: "/settings",
  },
];

// Long press duration in milliseconds
const LONG_PRESS_DURATION = 500;

// Configuration for pagination
const APPS_PER_PAGE = 6;

// Grid configuration
const GRID_COLS = 3;
const GRID_ROW_HEIGHT = 56;
const GRID_MARGIN: [number, number] = [6, 6];
const DEFAULT_GRID_WIDTH = 220;

interface AppFavoritesSectionProps {
  isCollapsed?: boolean;
}

// Convert apps to default grid layout
const appsToDefaultLayout = (apps: AppFavorite[]): GridLayout.Layout[] => {
  return apps.map((app, index) => ({
    i: app.id,
    x: index % GRID_COLS,
    y: Math.floor(index / GRID_COLS),
    w: 1,
    h: 1,
  }));
};

// Convert stored layout to GridLayout.Layout[]
const storedToGridLayout = (
  stored: AppLayoutItem[],
  apps: AppFavorite[]
): GridLayout.Layout[] => {
  if (stored.length === 0) {
    return appsToDefaultLayout(apps);
  }

  // Create a map of stored positions
  const storedMap = new Map(stored.map((item) => [item.i, item]));
  const appIds = new Set(apps.map((app) => app.id));

  // Build layout from stored, only for apps that still exist
  const layout: GridLayout.Layout[] = [];
  const usedPositions = new Set<string>();

  for (const item of stored) {
    if (appIds.has(item.i)) {
      layout.push({ ...item });
      usedPositions.add(`${item.x},${item.y}`);
    }
  }

  // Add any new apps that weren't in stored layout
  let nextIndex = layout.length;
  for (const app of apps) {
    if (!storedMap.has(app.id)) {
      layout.push({
        i: app.id,
        x: nextIndex % GRID_COLS,
        y: Math.floor(nextIndex / GRID_COLS),
        w: 1,
        h: 1,
      });
      nextIndex++;
    }
  }

  return layout;
};

// Convert GridLayout.Layout[] to AppLayoutItem[] for storage
const gridToStoredLayout = (layout: GridLayout.Layout[]): AppLayoutItem[] => {
  return layout.map((item) => ({
    i: item.i,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
  }));
};

// Get ordered apps from layout (sorted by position)
const getOrderedAppsFromLayout = (
  layout: GridLayout.Layout[],
  appMap: Map<string, AppFavorite>
): AppFavorite[] => {
  const sortedLayout = [...layout].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });
  return sortedLayout
    .map((item) => appMap.get(item.i))
    .filter((app): app is AppFavorite => app !== undefined);
};

const AppFavoritesSection: React.FC<AppFavoritesSectionProps> = ({
  isCollapsed = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [editModeApps, setEditModeApps] = useState<AppFavorite[]>([]);
  const [gridLayout, setGridLayout] = useState<GridLayout.Layout[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(DEFAULT_GRID_WIDTH);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const widthMeasured = useRef(false);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Get edit mode state and layout from store
  const isAppEditMode = useSidebarStore((state) => state.isAppEditMode);
  const appFavoritesLayout = useSidebarStore((state) => state.appFavoritesLayout);
  const setAppEditMode = useSidebarStore((state) => state.setAppEditMode);
  const setAppFavoritesLayout = useSidebarStore(
    (state) => state.setAppFavoritesLayout
  );

  // Split view state - used to determine active app based on focused panel
  // Use optimized hook that returns only the focused panel (not all panels)
  const focusedPanel = useFocusedPanel();
  const isSplitViewActive = useSplitViewActive();
  const updatePanel = useSplitViewStore((state) => state.updatePanel);

  // Create app map for lookups
  const appMap = useMemo(
    () => new Map(defaultAppFavorites.map((app) => [app.id, app])),
    []
  );

  // Compute ordered apps based on stored layout
  const orderedApps = useMemo(() => {
    if (appFavoritesLayout.length === 0) {
      return defaultAppFavorites;
    }
    return getOrderedAppsFromLayout(
      storedToGridLayout(appFavoritesLayout, defaultAppFavorites),
      appMap
    );
  }, [appFavoritesLayout, appMap]);

  // Initialize edit mode state - only when entering edit mode
  useEffect(() => {
    if (isAppEditMode && editModeApps.length === 0) {
      // Use stored layout or create default
      const initialLayout = storedToGridLayout(appFavoritesLayout, defaultAppFavorites);
      setGridLayout(initialLayout);
      setEditModeApps(defaultAppFavorites);
      widthMeasured.current = false;
    } else if (!isAppEditMode) {
      // Reset when exiting
      setEditModeApps([]);
      setGridLayout([]);
      widthMeasured.current = false;
    }
  }, [isAppEditMode, appFavoritesLayout, editModeApps.length]);

  // Measure container width after render
  useEffect(() => {
    if (isAppEditMode && containerRef.current && !widthMeasured.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth;
          if (width > 100) {
            setContainerWidth(width);
            widthMeasured.current = true;
          }
        }
      });
    }
  }, [isAppEditMode, editModeApps]);

  const totalPages = Math.ceil(orderedApps.length / APPS_PER_PAGE);

  // Navigation callbacks for gesture scroll
  const goToPrevious = useCallback(() => {
    if (currentPage > 0) setCurrentPage((p) => p - 1);
  }, [currentPage]);

  const goToNext = useCallback(() => {
    if (currentPage < totalPages - 1) setCurrentPage((p) => p + 1);
  }, [currentPage, totalPages]);

  // Use gesture scroll hook for vertical app pagination
  useGestureScroll(scrollContainerRef, {
    direction: "vertical",
    totalPages,
    currentIndex: currentPage,
    onPrevious: goToPrevious,
    onNext: goToNext,
    disabled: isAppEditMode,
  });

  const isAppActive = useCallback(
    (app: AppFavorite) => {
      // When split view is active, use the focused panel's type to determine active app
      if (isSplitViewActive && focusedPanel) {
        switch (app.id) {
          case "dashboards":
            return focusedPanel.type === "dashboard";
          case "trackers":
            return focusedPanel.type === "tracker" || focusedPanel.type === "trackers";
          case "enrichments":
            return focusedPanel.type === "enrichment" || focusedPanel.type === "enrichments";
          case "alerts":
            return focusedPanel.type === "alert" || focusedPanel.type === "alerts";
          case "chat":
            return focusedPanel.type === "chat";
          case "playground":
            return focusedPanel.type === "search";
          case "widgets":
            return focusedPanel.type === "widgets";
          case "branches":
            return focusedPanel.type === "branches";
          case "investigations":
            return focusedPanel.type === "investigation" || focusedPanel.type === "investigations";
          case "reports":
            return focusedPanel.type === "reports";
          // Account app types - check route for specific app
          case "plan":
          case "workspaces":
          case "organization":
          case "members":
          case "api_tokens":
          case "channels":
          case "settings":
            if (focusedPanel.type === "account" && focusedPanel.route) {
              return focusedPanel.route.startsWith(app.link.split("?")[0]);
            }
            return false;
          default:
            return false;
        }
      }

      // Fallback: use URL pathname (normal navigation mode)
      const [appPath] = app.link.split("?");
      const currentParams = new URLSearchParams(location.search);

      if (app.id === "dashboards") {
        return location.pathname.startsWith("/dashboards");
      }
      if (app.id === "widgets") {
        return location.pathname.startsWith("/widgets");
      }
      if (app.id === "branches") {
        return location.pathname.startsWith("/branches");
      }
      if (app.id === "investigations") {
        return location.pathname.startsWith("/investigations");
      }
      if (app.id === "reports") {
        return location.pathname.startsWith("/reports");
      }
      if (app.id === "enrichments") {
        return (
          location.pathname.startsWith("/trackers") &&
          currentParams.get("tab") === "enrichments"
        );
      }
      if (app.id === "trackers") {
        return (
          location.pathname.startsWith("/trackers") &&
          currentParams.get("tab") !== "enrichments"
        );
      }
      // Simple path matching for all other apps (including new account apps)
      return (
        location.pathname === appPath ||
        location.pathname.startsWith(appPath + "/")
      );
    },
    [location.pathname, location.search, isSplitViewActive, focusedPanel]
  );

  const getAppsForPage = useCallback(
    (pageIndex: number) => {
      const startIndex = pageIndex * APPS_PER_PAGE;
      return orderedApps.slice(startIndex, startIndex + APPS_PER_PAGE);
    },
    [orderedApps]
  );

  const handleLongPressStart = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    longPressTimerRef.current = setTimeout(() => {
      setAppEditMode(true);
    }, LONG_PRESS_DURATION);
  }, [setAppEditMode]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // Handle layout change from react-grid-layout
  const handleLayoutChange = useCallback((newLayout: GridLayout.Layout[]) => {
    setGridLayout(newLayout);
  }, []);

  // Save and close edit mode
  const handleCloseEditMode = useCallback(() => {
    // Save full layout to store
    const layoutToSave = gridToStoredLayout(gridLayout);
    setAppFavoritesLayout(layoutToSave);
    setAppEditMode(false);
    setIsDragging(false);
  }, [gridLayout, setAppFavoritesLayout, setAppEditMode]);

  // Handle Escape key
  useEffect(() => {
    if (!isAppEditMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseEditMode();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isAppEditMode, handleCloseEditMode]);

  // Exit edit mode on navigation
  useEffect(() => {
    if (isAppEditMode) {
      const layoutToSave = gridToStoredLayout(gridLayout);
      setAppFavoritesLayout(layoutToSave);
      setAppEditMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // Refs for app items for drag preview
  const appItemRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Handler for drag start on app items
  const handleAppDragStart = useCallback(
    (e: React.DragEvent, app: AppFavorite) => {
      const payload = createAppDragPayload(app.id, app.title, app.link);
      setDragPayload(e, payload);

      // Create visual drag preview (clone of the app item)
      const itemElement = appItemRefs.current.get(app.id);
      if (itemElement && e.dataTransfer.setDragImage) {
        const rect = itemElement.getBoundingClientRect();
        // Scale down to a small preview (max 200px width)
        const maxWidth = 200;
        const scale = Math.min(1, maxWidth / rect.width);
        const scaledWidth = rect.width * scale;
        const scaledHeight = rect.height * scale;
        
        // Create a wrapper that has the final scaled size
        const wrapper = document.createElement("div");
        wrapper.style.position = "absolute";
        wrapper.style.top = "-9999px";
        wrapper.style.left = "-9999px";
        wrapper.style.width = `${scaledWidth}px`;
        wrapper.style.height = `${scaledHeight}px`;
        wrapper.style.overflow = "hidden";
        wrapper.style.borderRadius = "8px";
        wrapper.style.boxShadow = "0 8px 24px rgba(0,0,0,0.25)";
        wrapper.style.border = "2px solid var(--arc-accent)";
        
        // Create a scaled clone inside the wrapper
        const clone = itemElement.cloneNode(true) as HTMLElement;
        clone.style.width = `${rect.width}px`;
        clone.style.height = `${rect.height}px`;
        clone.style.transform = `scale(${scale})`;
        clone.style.transformOrigin = "top left";
        clone.style.opacity = "0.95";
        clone.style.background = "var(--arc-sidebar-bg)";
        
        wrapper.appendChild(clone);
        document.body.appendChild(wrapper);
        
        // Set the drag image with offset to center it
        e.dataTransfer.setDragImage(wrapper, scaledWidth / 2, scaledHeight / 2);
        
        // Remove the wrapper after a short delay
        requestAnimationFrame(() => {
          document.body.removeChild(wrapper);
        });
      }
    },
    []
  );

  /**
   * Handler for app click - panel-aware navigation.
   * When split view is active, updates only the focused panel.
   * When split view is inactive, navigates globally.
   */
  const handleAppClick = useCallback(
    (e: React.MouseEvent, app: AppFavorite) => {
      if (isSplitViewActive && focusedPanel) {
        // In split view: update the focused panel's route
        e.preventDefault();
        
        const newType = getPanelTypeFromRoute(app.link.split("?")[0]);
        updatePanel(focusedPanel.id, {
          route: app.link,
          type: newType,
          title: app.title,
        });
        
        // Also update browser URL to match focused panel
        navigate(app.link, { replace: true });
      }
      // If not in split view, let the Link handle navigation normally
    },
    [isSplitViewActive, focusedPanel, updatePanel, navigate]
  );

  // Collapsed view
  if (isCollapsed) {
    return (
      <div className="px-2 py-2 flex flex-col items-center gap-1">
        {orderedApps.slice(0, 5).map((app) => {
          const isActive = isAppActive(app);

          return (
            <Link 
              key={app.id} 
              to={app.link} 
              title={app.title}
              draggable
              onDragStart={(e) => handleAppDragStart(e, app)}
              onClick={(e) => handleAppClick(e, app)}
            >
              <div
                ref={(el) => {
                  if (el) appItemRefs.current.set(app.id, el);
                }}
                className={`w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all hover:scale-110 active:scale-95 ${
                  isActive
                    ? "arc-sidebar-item-active-collapsed text-[var(--arc-sidebar-text-primary)]"
                    : "text-[var(--arc-sidebar-text-secondary)] hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)]"
                }`}
              >
                {app.icon}
              </div>
            </Link>
          );
        })}
        {orderedApps.length > 5 && (
          <div className="w-10 h-2 flex items-center justify-center gap-0.5">
            <span className="w-1 h-1 rounded-full bg-[var(--arc-sidebar-text-secondary)] opacity-50" />
            <span className="w-1 h-1 rounded-full bg-[var(--arc-sidebar-text-secondary)] opacity-50" />
            <span className="w-1 h-1 rounded-full bg-[var(--arc-sidebar-text-secondary)] opacity-50" />
          </div>
        )}
      </div>
    );
  }

  // Edit mode with react-grid-layout
  if (isAppEditMode && editModeApps.length > 0 && gridLayout.length > 0) {
    const numRows = Math.ceil(editModeApps.length / GRID_COLS);
    const gridHeight = numRows * GRID_ROW_HEIGHT + (numRows + 1) * GRID_MARGIN[1];

    return (
      <div className="px-3 py-2">
        <motion.div
          className="bg-[var(--arc-sidebar-accent-glow)] backdrop-blur-md rounded-xl p-2 relative overflow-hidden"
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[10px] font-medium text-[var(--arc-sidebar-text-secondary)]">
              Réorganiser
            </span>
            <button
              onClick={handleCloseEditMode}
              className="app-edit-done-button flex items-center gap-1"
            >
              <FaCheck size={10} />
              OK
            </button>
          </div>

          {/* Grid Layout Container */}
          <div 
            ref={containerRef} 
            className="app-edit-grid-container w-full"
            style={{ height: gridHeight }}
          >
            <GridLayout
              layout={gridLayout}
              cols={GRID_COLS}
              rowHeight={GRID_ROW_HEIGHT}
              width={containerWidth}
              margin={GRID_MARGIN}
              containerPadding={[0, 0]}
              onLayoutChange={handleLayoutChange}
              onDragStart={() => setIsDragging(true)}
              onDragStop={() => setIsDragging(false)}
              isDraggable={true}
              isResizable={false}
              compactType="vertical"
              useCSSTransforms={true}
            >
              {editModeApps.map((app) => {
                const isActive = isAppActive(app);

                return (
                  <div key={app.id}>
                    <div
                      className={`
                        flex flex-col items-center justify-center py-1.5 px-1 rounded-lg 
                        cursor-grab active:cursor-grabbing h-full select-none
                        ${
                          isActive
                            ? "arc-sidebar-item-active-collapsed text-[var(--arc-sidebar-text-primary)] shadow-sm"
                            : "text-[var(--arc-sidebar-text-secondary)] bg-[var(--arc-sidebar-accent-glow)]/50"
                        }
                        ${!isDragging ? "app-wobble" : ""}
                      `}
                    >
                      <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center mb-0.5 border border-[var(--arc-sidebar-border-accent)] ${
                          isActive
                            ? "arc-sidebar-item-active-collapsed"
                            : "bg-[var(--arc-sidebar-accent-glow)]"
                        }`}
                      >
                        {app.icon}
                      </div>
                      <span className="text-[9px] font-medium truncate w-full text-center leading-tight">
                        {app.title}
                      </span>
                    </div>
                  </div>
                );
              })}
            </GridLayout>
          </div>
        </motion.div>
      </div>
    );
  }

  // Normal view - paginated
  return (
    <div className="px-3 py-2" ref={scrollContainerRef}>
      <div className="bg-[var(--arc-sidebar-accent-glow)] backdrop-blur-md rounded-xl p-1.5 relative">
        <div className="overflow-hidden" style={{ height: "124px" }}>
          <div
            className="transition-transform duration-300 ease-out"
            style={{ transform: `translateY(-${currentPage * 124}px)` }}
          >
            {Array.from({ length: totalPages }).map((_, pageIndex) => (
              <div
                key={pageIndex}
                className="w-full"
                style={{ height: "124px" }}
              >
                <div className="grid grid-cols-3 grid-rows-2 gap-1 h-full">
                  {getAppsForPage(pageIndex).map((app) => {
                    const isActive = isAppActive(app);

                    return (
                      <Link
                        key={app.id}
                        to={app.link}
                        draggable
                        onDragStart={(e) => handleAppDragStart(e, app)}
                        onClick={(e) => handleAppClick(e, app)}
                        onPointerDown={handleLongPressStart}
                        onPointerUp={handleLongPressEnd}
                        onPointerLeave={handleLongPressEnd}
                        onPointerCancel={handleLongPressEnd}
                      >
                        <div
                          ref={(el) => {
                            if (el) appItemRefs.current.set(app.id, el);
                          }}
                          className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg cursor-pointer transition-all hover:scale-105 active:scale-95 h-full ${
                            isActive
                              ? "arc-sidebar-item-active-collapsed text-[var(--arc-sidebar-text-primary)] shadow-sm"
                              : "text-[var(--arc-sidebar-text-secondary)] hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)]"
                          }`}
                        >
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1 border border-[var(--arc-sidebar-border-accent)] transition-colors ${
                              isActive
                                ? "arc-sidebar-item-active-collapsed"
                                : "bg-[var(--arc-sidebar-accent-glow)]"
                            }`}
                          >
                            {app.icon}
                          </div>
<span className="text-[10px] font-medium truncate w-full text-center pb-px">
                            {app.title}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {totalPages > 1 && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 flex flex-col items-center gap-1.5">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index)}
                className={`transition-all ${
                  index === currentPage
                    ? "h-4 w-1.5 rounded-full bg-[var(--arc-accent)]"
                    : "h-1.5 w-1.5 rounded-full bg-[var(--arc-sidebar-text-secondary)] opacity-40 hover:opacity-70"
                }`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

AppFavoritesSection.displayName = "AppFavoritesSection";

export default AppFavoritesSection;
